import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { moneyBRL } from "@/utils/money";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Wrench,
  Receipt,
  AlertTriangle,
  Clock,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

type DashboardData = {
  kpis: {
    status: Record<string, number>;
    faturamentoHoje: any;
    faturamentoMes: any;
  };
  charts: {
    faturamento14d: Array<{ day: string; total: any }>;
  };
  lowStock: Array<{
    id: string;
    codigo?: string | null;
    descricao: string;
    estoqueAtual: any;
    estoqueMin: any;
  }>;
  recentOS: Array<{
    id: string;
    status: string;
    total: any;
    createdAt: string;
    cliente: { nome: string };
    veiculo?: { placa?: string | null; marca?: string | null; modelo?: string | null } | null;
  }>;
};

function toNumber(v: any) {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function shortId(id: string) {
  return id.slice(-6).toUpperCase();
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);

  async function load() {
    setLoading(true);
    try {
      const resp = await api.get("/api/dashboard/summary");
      setData(resp.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const status = data?.kpis?.status ?? {};
  const abertas = status["aberta"] ?? 0;
  const execucao = status["em_execucao"] ?? 0;
  const aguardando = status["aguardando_pecas"] ?? 0;
  const finalizadas = status["finalizada"] ?? 0;
  const faturadas = status["faturada"] ?? 0;

  const faturamentoHoje = moneyBRL(data?.kpis?.faturamentoHoje ?? 0);
  const faturamentoMes = moneyBRL(data?.kpis?.faturamentoMes ?? 0);

  const chartRows = useMemo(() => {
    const rows = data?.charts?.faturamento14d ?? [];
    return rows.map((r) => ({
      day: r.day.slice(5), // MM-DD
      total: toNumber(r.total),
    }));
  }, [data]);

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-600">
              Bem-vindo ao sistema da Auto Mecânica 🚗🔧
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading}>
              {loading ? "Atualizando..." : "Atualizar"}
            </Button>
            <Link to="/os">
              <Button className="gap-2">
                Ver OS <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Wrench size={14} /> OS Abertas
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{abertas}</div>
            <div className="text-sm text-slate-600 mt-2">
              Em execução: <b>{execucao}</b>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Clock size={14} /> Aguardando peças
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{aguardando}</div>
            <div className="text-sm text-slate-600 mt-2">
              Finalizadas: <b>{finalizadas}</b>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Receipt size={14} /> Faturadas
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{faturadas}</div>
            <div className="text-sm text-slate-600 mt-2">
              Receita hoje: <b>{faturamentoHoje}</b>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <TrendingUp size={14} /> Faturamento do mês
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{faturamentoMes}</div>
            <div className="text-sm text-slate-600 mt-2">
              Base: OS com status <b>faturada</b>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <AlertTriangle size={14} /> Alertas
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">
              {data?.lowStock?.length ?? 0}
            </div>
            <div className="text-sm text-slate-600 mt-2">
              Itens com estoque abaixo do mínimo
            </div>
          </div>
        </div>

        {/* Gráfico + Estoque baixo */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-semibold text-slate-900">Faturamento (últimos 14 dias)</div>
            <div className="text-sm text-slate-500 mb-3">
              Soma diária das OS faturadas
            </div>

            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="total" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {/* <div className="h-64 flex items-center justify-center text-slate-500">
              Gráfico em manutenção 🔧
            </div> */}

          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-semibold text-slate-900">Estoque baixo</div>
            <div className="text-sm text-slate-500 mb-3">
              Prioridade pra compra/reposição
            </div>

            {!data?.lowStock?.length ? (
              <div className="text-slate-500 text-sm py-8 text-center">
                Nenhum alerta agora ✅
              </div>
            ) : (
              <div className="space-y-3">
                {data.lowStock.map((p) => (
                  <div key={p.id} className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        {p.codigo ? `${p.codigo} • ` : ""}{p.descricao}
                      </div>
                      <div className="text-xs text-slate-500">
                        Atual: {String(p.estoqueAtual)} | Mín: {String(p.estoqueMin)}
                      </div>
                    </div>
                    <Link to="/pecas">
                      <Button size="sm" variant="outline">Ver</Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Últimas OS */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">Últimas OS</div>
              <div className="text-sm text-slate-500">Acesso rápido</div>
            </div>
            <Link to="/os">
              <Button variant="outline">Ver todas</Button>
            </Link>
          </div>

          {!data?.recentOS?.length ? (
            <div className="p-8 text-center text-slate-500">Sem dados ainda.</div>
          ) : (
            <div className="divide-y">
              {data.recentOS.map((os) => (
                <Link key={os.id} to={`/os/${os.id}`} className="block hover:bg-slate-50">
                  <div className="p-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-slate-900">
                        OS {shortId(os.id)} • {os.cliente?.nome}
                      </div>
                      <div className="text-sm text-slate-500">
                        {os.veiculo?.placa ? `${os.veiculo.placa} • ` : ""}
                        {os.veiculo?.marca ?? ""} {os.veiculo?.modelo ?? ""}
                      </div>
                      <div className="text-xs text-slate-400">
                        {new Date(os.createdAt).toLocaleString("pt-BR")}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm text-slate-500">Status</div>
                      <div className="font-semibold text-slate-900">{os.status}</div>
                      <div className="text-sm text-slate-700 mt-1">
                        {moneyBRL(os.total)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
